import { useTrackers } from '@ps/web-shared-analytics';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { WidgetFC } from '@swf/core/types/Widget';
import { EventCard, TryAgainMessage } from '@swf/sports/components';
import { Breadcrumb } from '@swf/sports/components/Breadcrumb/Breadcrumb';
import useLiveDataPolling from '@swf/sports/hooks/useLiveDataPolling/useLiveDataPolling';

import styles from './SportsEventInfoWidget.scss';
import { BvpPlayer } from './components/BvpPlayer/BvpPlayer';
import LiveOptions from './components/LiveOptions/LiveOptions';
import LoadingState from './components/LoadingState/LoadingState';
import { sportsEventInfoWidgetModel, useSportsEventInfoWidget } from './model';
import { type StreamType } from './types';

interface BvpPanelProps {
  eventId: number;
  eventTypeId: number;
  name: string;
  streamType?: StreamType;
}

const BvpPanel: React.FC<BvpPanelProps> = ({ eventId, eventTypeId, name, streamType }) => (
  <div className={styles.bvpContainer}>
    <BvpPlayer
      eventId={eventId}
      name={name}
      eventTypeId={eventTypeId}
      data-testid="bvp-iframe"
      streamType={streamType}
    />
  </div>
);

const SportsEventInfoWidget: WidgetFC = () => {
  const { widgetSelect } = useSportsEventInfoWidget();
  const eventInfo = useSelector(widgetSelect.eventInfo);
  const isLoading = useSelector(widgetSelect.isLoading);
  const errorCode = useSelector(widgetSelect.errorCode);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const [showLiveData, setShowLiveData] = useState(false);
  const [showLiveStream, setShowLiveStream] = useState(false);
  const [activeToggle, setActiveToggle] = useState<string | null>(null);
  const { panelToggled } = useTrackers();

  const eventTypesEventIdsMap = useMemo(
    () =>
      eventInfo && eventInfo.inPlay
        ? {
            [eventInfo.eventTypeId]: [eventInfo.eventId]
          }
        : {},
    [eventInfo]
  );

  const handleToggleGroupChange = (value: string) => {
    if (value) {
      setShowLiveData(value === 'Live Data');
      setShowLiveStream(value === 'Live Stream');
      setActiveToggle(value);
      panelToggled({
        panelName: value,
        isOpen: true
      });
    } else {
      setShowLiveData(false);
      setShowLiveStream(false);
      setActiveToggle(null);
      panelToggled({
        panelName: activeToggle ?? value,
        isOpen: false
      });
    }

    // this handles switching between live data and live stream when one is already open
    if (value && activeToggle && activeToggle !== value) {
      panelToggled({
        panelName: activeToggle,
        isOpen: false
      });
    }
  };

  useLiveDataPolling({ eventTypesEventIdsMap });

  useEffect(() => {
    if (!eventInfo) {
      return;
    }

    if (!eventInfo.hasLiveDataAvailable && !eventInfo.hasLiveStreamAvailable) {
      setShowLiveData(false);
      setShowLiveStream(false);
      setActiveToggle(null);
    }
  }, [eventInfo]);

  if (isLoading && !hasPreloaded) {
    return <LoadingState />;
  }

  if (errorCode) {
    if (errorCode === 'NOT_FOUND' && eventInfo) {
      const {
        offeredInPlay,
        eventId,
        eventTypeId,
        name,
        hasLiveDataAvailable,
        hasLiveStreamAvailable
      } = eventInfo;
      return (
        <>
          <Breadcrumb fallbackBreadcrumbText={eventInfo.competitionName} />
          <EventCard {...eventInfo} offeredInPlay={offeredInPlay} eventStatusOverride="COMPLETE">
            <LiveOptions
              handleToggleGroupChange={handleToggleGroupChange}
              hasLiveDataAvailable={hasLiveDataAvailable}
              hasLiveStreamAvailable={hasLiveStreamAvailable}
            />
          </EventCard>
          {showLiveData || showLiveStream ? (
            <BvpPanel
              eventId={eventId}
              eventTypeId={eventTypeId}
              name={name}
              streamType={showLiveData ? 'viz' : 'vid'}
            />
          ) : null}
        </>
      );
    }

    if (errorCode === 'NOT_FOUND') {
      return null;
    }

    return <TryAgainMessage />;
  }

  if (!eventInfo) {
    return null;
  }

  const { offeredInPlay, eventId, name, hasLiveDataAvailable, hasLiveStreamAvailable, ...rest } =
    eventInfo;

  return (
    <>
      <Breadcrumb fallbackBreadcrumbText={eventInfo.competitionName} />
      <EventCard eventId={eventId} name={name} {...rest} offeredInPlay={offeredInPlay}>
        <LiveOptions
          handleToggleGroupChange={handleToggleGroupChange}
          hasLiveDataAvailable={hasLiveDataAvailable}
          hasLiveStreamAvailable={hasLiveStreamAvailable}
        />
      </EventCard>
      {showLiveData || showLiveStream ? (
        <BvpPanel
          eventId={eventId}
          eventTypeId={eventInfo.eventTypeId}
          name={name}
          streamType={showLiveData ? 'viz' : 'vid'}
        />
      ) : null}
    </>
  );
};

SportsEventInfoWidget.model = sportsEventInfoWidgetModel;

export default withWidgetTestId(SportsEventInfoWidget, 'sports-event-info');
